import React, { useCallback, useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { Dialog, Paper, Typography, Box } from '@mui/material';
import { useCommandPalette } from 'contextProviders/CommandPaletteProvider';
import { useCommandPaletteSearch } from 'hooks/commandPalette/useCommandPaletteSearch';
import { useKeyboardNavigation } from 'hooks/commandPalette/useKeyboardNavigation';
import CommandPaletteInput from './CommandPaletteInput';
import CommandPaletteResults from './CommandPaletteResults';
import CommandPaletteFooter from './CommandPaletteFooter';

/**
 * Error boundary that catches rendering errors inside the command palette
 * without crashing the rest of the app.
 */
class CommandPaletteErrorBoundary extends Component<
  { children: ReactNode; onReset: () => void },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[CommandPalette] Rendering error:', error, info.componentStack);
  }

  componentDidUpdate(prevProps: { children: ReactNode; onReset: () => void }) {
    // Reset error state when the palette is re-opened
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="error">
            Something went wrong. Press Esc and try again.
          </Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

const CommandPaletteContent: React.FC = () => {
  const { isOpen, close } = useCommandPalette();
  const { query, setQuery, mode, flatResults, isLoading } = useCommandPaletteSearch(isOpen);

  const handleSelect = useCallback(
    (index: number) => {
      const result = flatResults[index];
      if (result && typeof result.onSelect === 'function') {
        result.onSelect();
        close();
      }
    },
    [flatResults, close],
  );

  const { activeIndex, setActiveIndex, handleKeyDown, listRef } = useKeyboardNavigation({
    resultCount: flatResults.length,
    onSelect: handleSelect,
    isOpen,
  });

  // Reset query when palette closes
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen, setQuery]);

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      maxWidth="sm"
      fullWidth
      aria-label="Search commands and content"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          pt: '15vh',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' },
        },
      }}
      disableRestoreFocus={false}
    >
      <Paper
        elevation={8}
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        <CommandPaletteInput
          query={query}
          mode={mode}
          onChange={setQuery}
          onKeyDown={handleKeyDown}
          resultCount={flatResults.length}
          activeIndex={activeIndex}
        />
        <CommandPaletteErrorBoundary onReset={() => setQuery('')}>
          <CommandPaletteResults
            results={flatResults}
            activeIndex={Math.min(activeIndex, Math.max(0, flatResults.length - 1))}
            onSelect={handleSelect}
            onHover={setActiveIndex}
            listRef={listRef}
            isLoading={isLoading}
            query={query}
          />
        </CommandPaletteErrorBoundary>
        <CommandPaletteFooter />
      </Paper>
    </Dialog>
  );
};

const CommandPalette: React.FC = () => {
  const { close } = useCommandPalette();
  return (
    <CommandPaletteErrorBoundary onReset={close}>
      <CommandPaletteContent />
    </CommandPaletteErrorBoundary>
  );
};

export default CommandPalette;
